import UPServices from "@/services/user-preferences.js";

const NOTIFICATION = "notification";

const state = {
    isLoadingPref: true,
    prefs: {} // there will be more 'keys' later. ie. "calendar", "task", "notification"
};

const getters = {
    prefs(state) {
        return state.prefs;
    }
};

const actions = {
    /**
     * returns prefs from state
     */
    getPref({ state }, payload = {}) {
        try {
            if (payload.category) {
                return state.prefs?.[payload.category] ?? {};
            } else {
                return state.prefs;
            }
        } catch (error) {
            console.log(error);
            return null;
        }
    },
    /**
     * update remote db
     */
    async updatePref({ state }, payload) {
        try {
            if (!payload.category) {
                throw new Error("Please provide category key.");
            }

            return await UPServices.updatePreferences(
                payload.category,
                payload.data
            );
        } catch (error) {
            console.log(error);
            return null;
        }
    },
    /**
     * fetch user preferences from backend
     * then keep them in global store
     */
    async initGetPrefs(store) {
        store.commit("setLoading", true);
        let prefs = await UPServices.getPreferences();
        let master = {};

        for (let pref of prefs) {
            if (!pref.category) {
                continue;
            }

            let data = null;

            try {
                let jsonString = pref?.data?.[0] ?? "";

                if (pref?.category === NOTIFICATION) {
                    data = generateProperNotificationObject(jsonString);
                } else {
                    data = JSON.parse(jsonString);
                }
            } catch (error) {
                data = {};
            }

            master[pref.category] = data;
        }

        // if has no calendar default object
        if (!master.calendar) {
            master.calendar = calendarDefaultObject();
        }

        // if has no soap default object
        if (!master.soap) {
            master.soap = soapDefaultObject();
        }

        store.commit("setAllPrefs", master);
        store.commit("setLoading", false);
    },
    updateLocalPref(store, { data, isDataString = false, type }) {
        let properJSON = null;

        try {
            if (isDataString) {
                if (type === NOTIFICATION) {
                    properJSON = JSON.parse(`[${data}]`);
                } else {
                    properJSON = JSON.parse(data);
                }
            } else {
                properJSON = data;
            }
        } catch (error) {
            return;
        }

        store.commit("setSpecificPref", {
            type: type,
            data: properJSON
        });
    },
    isShowNotificationBanner({ state }, type) {
        let prefs = state?.prefs?.notification ?? [];
        let theType = prefs.find(p => p.type === type);
        return theType?.show_banner ?? true;
    }
};

const mutations = {
    setLoading(state, value) {
        state.isLoadingPref = value;
    },
    setCalendarAppointmentPref(state, data) {
        state.prefs.calendar = {
            ...state.prefs.calendar,
            ...data
        };
    },
    setSpecificPref(state, { type, data }) {
        state.prefs[type] = { ...state.prefs[type], ...data };
    },
    setAllPrefs(state, data = {}) {
        state.prefs = { ...data };
    }
};

export default {
    namespaced: true,
    state,
    actions,
    mutations,
    getters
};

function calendarDefaultObject() {
    return {
        selectedResource: null,
        selectedColumnId: null,
        isShowColumnStaff: true,
        viewOptions: {
            today: {
                enabled: true
            },
            day: {
                enabled: false,
                date: null
            },
            week: {
                enabled: false,
                date: null
            }
        }
    };
}

function soapDefaultObject() {
    return {
        left_navigation: false
    };
}

/**
 * Case for user preferences (notification):
 *
 * 	1.	String, but not proper (no wrapper […])
 * 	2.	String, but proper (can be directly JSON parsed without issues)
 * 	3.	Object (no need to parse JSON)
 *
 * So I created this function to handle all scenarios to avoid rendering issues when loading user preferences data.
 *
 * Why can it be different? Honestly, I don’t know how it get stored in dynamodb. - Luqman
 */
function generateProperNotificationObject(src) {
    try {
        if (Array.isArray(src)) {
            return src;
        }

        if (typeof src === "string") {
            try {
                return JSON.parse(src); // try parsing the string as is (proper json string)
            } catch (e) {
                return JSON.parse(`[${src}]`); // if parsing fails, try adding brackets and then parse (improper json string)
            }
        }
    } catch (error) {
        return [];
    }
    return [];
}
